import React, { useEffect, useRef, useState } from "react";
import {
  Modal,
  Box,
  Button,
  Typography,
  Stack,
  FormControl,
  Avatar,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  CircularProgress,
} from "@mui/material";
import { CustomButton } from "../../../components/Button";
import TextInput from "../../../components/TextInput";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { useForm } from "react-hook-form";
import { ReactComponent as CameraIcon } from "../../../assets/images/camera-01.svg";
import { ReactComponent as ImageUpload } from "../../../assets/images/Image-Upload.svg";
import { ReactComponent as DeepseekIcon } from "../../../assets/images/deepseek.svg";
import { errorToast } from "../../../utils/toast";
import "./index.css";
import { RiOpenaiFill } from "react-icons/ri";
import { RiClaudeFill } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import { setOnboarding } from "../../../rtkSlices/onboardingSlice";
import { useUploadProfilePictureMutation } from "../../../services/user";

const AboutYou = (props: { setAboutYouForm: (values: any) => void }) => {
  const {
    register,
    resetField,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isValid, isSubmitting },
  } = useForm();

  const dispatch = useDispatch();
  const metaData = useSelector((state: any) => state.metaData);
  const onboarding = useSelector((state: any) => state.onboarding);
  const username = metaData.userInfo["cognito:username"];
  const theme = useTheme();
  const [uploadProfilePicture, uploadProfilePictureResult] =
    useUploadProfilePictureMutation();
  const [selectedProfilePic, setSelectedProfilePic] = useState(
    onboarding.onboarding.profilePicture
  );
  const [llm, setLlm] = React.useState(onboarding.onboarding.llm);
  const languages = [
    "Arabic",
    "Bengali",
    "Cantonese (Traditional Chinese)",
    "Chinese (Mandarin)",
    "Dutch",
    "English",
    "French",
    "German",
    "Hindi",
    "Italian",
    "Japanese",
    "Korean",
    "Malay (Bahasa Malaysia)",
    "Persian (Farsi)",
    "Polish",
    "Portuguese (European)",
    "Portuguese (Brazilian)",
    "Punjabi",
    "Russian",
    "Spanish",
    "Swahili",
    "Tamil",
    "Telugu",
    "Thai",
    "Turkish",
    "Urdu",
    "Vietnamese",
  ];
  const formValues = watch();

  useEffect(() => {
    const { name, nickname, age, occupation, location } = formValues;
    if (name || nickname || age || occupation || location) {
      props.setAboutYouForm({ ...formValues, llm });
    }
  }, [formValues, llm]);

  useEffect(() => {
    reset(onboarding.onboarding, { keepDirtyValues: true, keepErrors: true });
    if (onboarding.onboarding.profilePicture) {
      setSelectedProfilePic(onboarding.onboarding.profilePicture);
    }
  }, [onboarding]);

  const fields = [
    { label: "Name", field: "name" },
    { label: "Nickname", field: "nickname" },
    { label: "Location", field: "location" },
    { label: "Age", field: "age" },
    { label: "Current/Desired Occupation", field: "occupation" },
  ];

  function getExtension(filename: string) {
    return filename.split(".").pop();
  }

  const updateProfilePicture = async (file: string | Blob) => {
    const formData = new FormData();
    formData.append("file", file);
    const response: any = await uploadProfilePicture({
      body: formData,
      params: `?username=${username}`,
    });
    if (response.error) {
      errorToast(
        response.error.error ||
          (response.error.data && response.error.data.message)
      );
      setSelectedProfilePic("");
    } else {
      dispatch(setOnboarding({ profilePicture: response.data.imageUrl }));
    }
  };

  const selectProfilePic = (file: any) => {
    const ext = getExtension(file.name);
    if (ext == "jpg" || ext == "png" || ext == "svg" || ext == "jpeg") {
      setSelectedProfilePic(URL.createObjectURL(file));
      updateProfilePicture(file);
    } else {
      errorToast("Invalid file type");
    }
  };

  const personalField = (label: string, field: string) => {
    return (
      <FormControl variant="standard">
        <TextInput
          label={label}
          //   placeholder={label}
          ariaLabel={field}
          iconHandler={() => resetField(field)}
          register={register}
          icon={
            <HighlightOffIcon sx={{ color: "#2A945F", fontSize: "20px" }} />
          }
          required={true}
          name={field}
          borderColor={errors.name && "#e11c1cb3"}
          helperText={errors.name ? "Required field" : ""}
          width="450px"
          color={theme.palette.primary.main}
        />
      </FormControl>
    );
  };

  const languageField = () => {
    return (
      <FormControl
        fullWidth
        variant="outlined"
        sx={{
          "& .MuiOutlinedInput-root": {
            height: 40,
            borderRadius: "8px",
            color: "white",
            fontFamily: "Poppins",
            "& fieldset": {
              borderColor: "rgba(52, 180, 116, 0.5)",
              borderWidth: "2px",
              borderRadius: "5px",
            },
            "&:hover fieldset": {
              borderColor: "rgba(52, 180, 116, 0.5)",
              borderWidth: "2px",
              borderRadius: "5px",
            },
            "&.Mui-focused fieldset": {
              borderColor: "rgba(52, 180, 116, 0.5)",
              borderWidth: "2px",
              borderRadius: "5px",
            },
          },
          "& .MuiInputLabel-root": {
            color: "rgba(52, 180, 116, 0.5)",
            fontFamily: "Poppins",
            transform: "translate(14px, 9px) scale(1)",
            fontSize: "14px",
          },
          "& .MuiInputLabel-root.Mui-focused, & .MuiInputLabel-root.MuiFormLabel-filled":
            {
              transform: "translate(14px, -6px) scale(0.75)",
              color: "rgba(52, 180, 116, 0.5)",
            },
          "& .MuiSelect-icon": {
            color: "rgba(52, 180, 116, 0.5)",
            top: "calc(50% - 12px)",
          },
        }}
      >
        <InputLabel id="language-label" shrink>
          Preferred Language*
        </InputLabel>
        <Select
          labelId="language-label"
          id="language"
          defaultValue={onboarding.onboarding.language}
          label="Preferred Language*"
          {...register("language")}
          MenuProps={{
            disablePortal: true,
          }}
        >
          <MenuItem value="">
            <em>Select language</em>
          </MenuItem>
          {languages.map((language) => {
            return (
              <MenuItem
                selected={language == onboarding.onboarding.language}
                value={language}
              >
                {language}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    );
  };

  return (
    <Box className="about-section">
      <Typography fontSize={"53px"} fontFamily={"Fredoka"} fontWeight={"600"}>
        About YOU
      </Typography>
      <br />
      <Typography fontSize={"24px"} fontFamily={"Poppins"} fontWeight={"400"}>
        Bo wants to know you
      </Typography>

      <div className="profile-pic">
        {selectedProfilePic ? (
          <Avatar
            sx={{
              textTransform: "uppercase",
              fontWeight: "600",
              width: "143px",
              height: "143px",
              fontSize: "52px",
              backgroundColor: "#2a945f",
              opacity: uploadProfilePictureResult.isLoading ? "0.5" : "1",
            }}
            src={selectedProfilePic}
          />
        ) : (
          <ImageUpload className="image-upload" />
        )}
        {uploadProfilePictureResult.isLoading ? (
          <div className="profilepic-loader">
            <CircularProgress />
          </div>
        ) : null}
        <CameraIcon
          className={
            uploadProfilePictureResult.isLoading || selectedProfilePic
              ? "cameraicon2"
              : "cameraicon"
          }
          onClick={() => document.getElementById("profilepic-file")?.click()}
        />
        <input
          type="file"
          id="profilepic-file"
          onChange={(e) => {
            if (e.target && e.target.files) {
              selectProfilePic(e.target.files[0]);
            }
          }}
        />
      </div>
      <br />
      <Stack spacing={2}>
        {fields
          .slice(0, 2)
          .map((field) => personalField(field.label, field.field))}
        {languageField()}
        {fields
          .slice(2)
          .map((field) => personalField(field.label, field.field))}

        <div
          style={{
            display: "flex",
            gap: "4px",
            alignItems: "center",
          }}
        >
          <Typography>Select LLM</Typography>
        </div>
        <div
          style={{
            display: "flex",
            gap: "4px",
            alignItems: "center",
            margin: "5px 0px",
          }}
        >
          <CustomButton
            text={"OpenAI"}
            color={theme.palette.primary.main}
            submit={() => {
              setLlm("openai");
              props.setAboutYouForm({ ...formValues, llm: "openai" });
            }}
            fontWeight="200"
            fontSize="13px"
            type="button"
            bgcolor={
              llm == "openai"
                ? "rgba(42, 148, 95, 1)"
                : "rgba(52, 180, 116, 0.2)"
            }
            borderRadius={"7px"}
            startIcon={<RiOpenaiFill color={theme.palette.primary.main} />}
          />
          <CustomButton
            text={"Claude"}
            color={theme.palette.primary.main}
            submit={() => {
              setLlm("claude");
              props.setAboutYouForm({ ...formValues, llm: "claude" });
            }}
            fontWeight="200"
            fontSize="13px"
            type="button"
            bgcolor={
              llm == "claude"
                ? "rgba(42, 148, 95, 1)"
                : "rgba(52, 180, 116, 0.2)"
            }
            borderRadius={"7px"}
            startIcon={<RiClaudeFill color={theme.palette.primary.main} />}
          />
          <CustomButton
            text={"DeepSeek"}
            color={theme.palette.primary.main}
            submit={() => {
              setLlm("deepseek");
              props.setAboutYouForm({ ...formValues, llm: "deepseek" });
            }}
            fontWeight="200"
            fontSize="13px"
            type="button"
            bgcolor={
              llm == "deepseek"
                ? "rgba(42, 148, 95, 1)"
                : "rgba(52, 180, 116, 0.2)"
            }
            borderRadius={"7px"}
            startIcon={<DeepseekIcon className="deepseek" />}
          />
        </div>
      </Stack>
    </Box>
  );
};

export default AboutYou;
